import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useNavigate,
  useParams,
  useSearch,
} from '@tanstack/react-router';
import { App, usePublishSelection } from './App';
import { BrowsePage, type BrowseSearch } from './routes/BrowsePage';
import { CollectionsPage } from './routes/CollectionsPage';
import { CollectionPage } from './routes/CollectionPage';
import { AdminUsers } from './routes/admin/AdminUsers';
import { AdminFiles } from './routes/admin/AdminFiles';
import { AdminLogs } from './routes/admin/AdminLogs';
import type { ItemSort, SortKey } from './api/types';

const SORTS = new Set<SortKey>(['newest', 'oldest', 'largest', 'smallest', 'name']);
const ITEM_SORTS = new Set<ItemSort>(['position', 'taken', 'newest', 'name']);

/**
 * Optional keys, deliberately. If these were declared as `sort: ItemSort |
 * undefined` the router would treat them as required and every <Link> to the
 * route would have to pass them.
 */
interface CollectionSearch {
  sort?: ItemSort;
  page?: number;
}

/**
 * Search-param parsing.
 *
 * Anything unrecognised falls back to the default rather than erroring, so a
 * stale bookmark or a hand-edited URL still renders something.
 */
function parseBrowseSearch(search: Record<string, unknown>): BrowseSearch {
  const sort = String(search.sort ?? '');
  const view = String(search.view ?? '');
  const page = Number(search.page);
  return {
    q: typeof search.q === 'string' && search.q ? search.q.slice(0, 200) : undefined,
    sort: SORTS.has(sort as SortKey) ? (sort as SortKey) : undefined,
    page: Number.isInteger(page) && page > 1 ? page : undefined,
    view: view === 'list' || view === 'grid' ? view : undefined,
  };
}

const rootRoute = createRootRoute({
  component: () => (
    <App>
      <Outlet />
    </App>
  ),
});

/** Shared by the three browse routes so the search-param plumbing is written once. */
function Browse({ folderId, scope }: { folderId: string | null; scope: 'mine' | 'shared' }) {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as BrowseSearch;
  const publishSelection = usePublishSelection();

  return (
    <BrowsePage
      folderId={folderId}
      scope={scope}
      search={search}
      onSearchChange={(next) => void navigate({ to: '.', search: next as never, replace: true })}
      onSelectionChange={publishSelection}
    />
  );
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: parseBrowseSearch,
  component: () => <Browse folderId={null} scope="mine" />,
});

const folderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/folders/$folderId',
  validateSearch: parseBrowseSearch,
  component: function FolderView() {
    const { folderId } = useParams({ from: '/folders/$folderId' });
    return <Browse folderId={folderId} scope="mine" />;
  },
});

const sharedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shared',
  validateSearch: parseBrowseSearch,
  component: () => <Browse folderId={null} scope="shared" />,
});

const collectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/collections',
  component: CollectionsPage,
});

const collectionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/collections/$collectionId',
  validateSearch: (search: Record<string, unknown>): CollectionSearch => {
    const sort = String(search.sort ?? '');
    const page = Number(search.page);
    return {
      sort: ITEM_SORTS.has(sort as ItemSort) ? (sort as ItemSort) : undefined,
      page: Number.isInteger(page) && page > 1 ? page : undefined,
    };
  },
  component: function CollectionView() {
    const { collectionId } = useParams({ from: '/collections/$collectionId' });
    const search = useSearch({ from: '/collections/$collectionId' });
    const navigate = useNavigate();
    return (
      <CollectionPage
        collectionId={collectionId}
        sort={search.sort ?? 'position'}
        page={search.page ?? 1}
        onSearchChange={(next) =>
          void navigate({ to: '.', search: (current) => ({ ...current, ...next }), replace: true })
        }
      />
    );
  },
});

const adminUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/users',
  component: AdminUsers,
});

const adminFilesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/files',
  component: AdminFiles,
});

const adminLogsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/logs',
  component: AdminLogs,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  folderRoute,
  sharedRoute,
  collectionsRoute,
  collectionRoute,
  adminUsersRoute,
  adminFilesRoute,
  adminLogsRoute,
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
